import { AlertTriangle, Bot, Check, ShieldCheck, Wrench, X } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError } from "@/lib/api";

/** The three tiers, in the order the MCP server writes them. */
const READ = "factorium:read";
const ACT = "factorium:act";
const OPS = "factorium:ops";

/**
 * SEC-5 — where a human approves an agent (M9.1).
 *
 * The MCP server sends a browser here, and this is the only moment in the whole flow where a
 * person is involved. It is what makes the feature's own sentence true — *"a human user needs
 * to login for an agent to be able to access data"* — and it is here rather than on the MCP
 * server because sign-in already lives in this app (SEC-1) and that server deliberately holds
 * no Supabase secret.
 *
 * **Outside the shell, like Transcript Review.** There is no rail and no navigator: somebody
 * arriving here came from another application to answer one question, and offering to take
 * them somewhere else mid-decision is the wrong thing to put on the screen. Signing in, if
 * they are not, is the app's ordinary gate around this route — an unauthenticated visitor
 * gets the sign-in screen and lands back here.
 *
 * **It says what the agent will be able to read, in the vocabulary the rest of the app uses.**
 * An approval nobody understands is a click-through, and the point of a consent screen is that
 * it is not one.
 *
 * **It reads the scopes and renders one block per tier (SEC-7).** A screen that said "read your
 * knowledge" while approving a token that can approve insights and re-run the pipeline would be
 * the worst thing in this flow, so the bullets are derived from the scopes rather than written
 * once and hoped over. What is sent to the Server is the list this screen parsed — not the raw
 * query string — so what was approved is what was displayed.
 *
 * **`chooser` is the one case where the scopes are not fixed by the link (M9.6+).** `/authorize`
 * sets it when an agent asked for nothing at all, or — checked against Claude Desktop rather
 * than assumed — asked for literally everything this deployment publishes in its own discovery
 * documents, which is what a client that just echoes `scopes_supported` back produces and is not
 * evidence anybody decided anything. Either way this screen gets the *whole* set the deployment
 * offers to choose from, rather than the usual fixed list. A client that named something
 * narrower — read alone, say — still gets exactly that, with no picker and no way to widen it.
 * The `scope` query param means two different things depending on `chooser`: the fixed grant,
 * or the menu to choose from.
 */
export function AuthorizeAgentPage() {
  const { api, organization } = useApp();
  const [params] = useSearchParams();
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const clientId = params.get("client_id");
  const request = params.get("request");
  const redirect = params.get("redirect");
  const name = params.get("client_name")?.trim() || "An agent";
  const chooser = params.get("chooser") === "1";

  // Only the tiers this screen can describe. Anything else is dropped rather than shown as a
  // scope string nobody can evaluate — and if that leaves nothing, the link is treated as
  // incomplete, because approving a request the screen cannot describe is the click-through
  // this page exists to prevent. In chooser mode this is the menu; otherwise it is the grant.
  const offered = (params.get("scope") ?? READ)
    .split(" ")
    .filter((scope) => scope === READ || scope === ACT || scope === OPS);

  // Read is the one tier every agent before the tiers already assumed, so it starts checked;
  // nothing else does, because ticking a box is a decision and defaulting it checked would not
  // be one. Only meaningful in chooser mode — otherwise `scopes` below ignores this entirely.
  const [chosen, setChosen] = useState<string[]>(() => (offered.includes(READ) ? [READ] : []));

  // Granting the developer tier is an owner's or an admin's act, not an ordinary member's —
  // the same line `is_org_admin` (0034) already draws for changing what the organization says
  // about itself, because a re-run or a step reconfiguration reaches every recording and every
  // member after it, not only the one approving. The Server checks this again, against
  // membership on file, before it ever signs an assertion — this filter is what keeps the
  // screen from offering, or the fixed list from showing, a promise the Server would refuse.
  const canGrantOps = organization?.role === "owner" || organization?.role === "admin";
  const scopes = (chooser ? chosen : offered).filter((scope) => scope !== OPS || canGrantOps);

  const incomplete = !clientId || !request || !redirect || offered.length === 0;
  const nothingChosen = chooser && chosen.length === 0;

  function toggle(scope: string) {
    setChosen((was) => (was.includes(scope) ? was.filter((one) => one !== scope) : [...was, scope]));
  }

  async function approve() {
    if (incomplete || nothingChosen) return;

    setWorking(true);
    setProblem(null);

    try {
      const granted = await api.approveAgent({ clientId, request, redirect, scopes });

      // Back to the MCP server, carrying the assertion. A full page navigation rather than a
      // router push: the destination is another application.
      const target = new URL(granted.redirect);
      target.searchParams.set("request", request);
      target.searchParams.set("assertion", granted.assertion);
      window.location.assign(target.toString());
    } catch (failure) {
      setWorking(false);
      setProblem(
        failure instanceof ApiError
          ? failure.message
          : "That approval could not be completed. Close this window and try connecting again.",
      );
    }
  }

  return (
    // This page is outside the shell (see the class doc above), so it has none of the
    // shell's own scrolling content frames — `body { overflow: hidden }` is otherwise the
    // whole page. `main` is the one that scrolls here, sized to the viewport rather than
    // merely a minimum of it; the inner wrapper centers the card with `min-h-full` rather
    // than a fixed height, so when the card is taller than the window — a short browser
    // window with the chooser's extra checkboxes and bullets was the case that first
    // showed this — the wrapper simply grows to fit it and normal top-to-bottom scrolling
    // reaches every part of it, including the button at the bottom. A fixed-height flex
    // parent with `items-center` would instead clip whatever does not fit, with nothing
    // to scroll to reveal it.
    <main className="h-dvh overflow-y-auto bg-[var(--surface-0)]">
      <div className="flex min-h-full items-center justify-center px-[var(--space-6)] py-[var(--space-8)]">
        <div className="w-full max-w-[520px] rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-1)] p-[var(--space-8)]">
          {incomplete ? (
            <Incomplete />
          ) : (
            <>
              <div className="flex items-center gap-[var(--space-3)]">
                <span className="flex size-10 items-center justify-center rounded-full bg-[var(--primary)]/12 text-[var(--primary)]">
                  <Bot className="size-5" aria-hidden />
                </span>
                <div>
                  <h1 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
                    {name} wants to {scopes.includes(ACT) || scopes.includes(OPS)
                      ? "work with your knowledge"
                      : "read your knowledge"}
                  </h1>
                  <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
                    Connecting through Factorium&rsquo;s agent interface
                  </p>
                </div>
              </div>

              {chooser && (
                <div className="mt-[var(--space-5)] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] p-[var(--space-4)]">
                  <p className="text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">
                    {name} did not ask for a specific level of access. Choose what to allow:
                  </p>
                  <div className="mt-[var(--space-3)] flex flex-col gap-[var(--space-2)]">
                    {offered.includes(READ) && (
                      <ScopeOption
                        label="Read your knowledge"
                        checked={chosen.includes(READ)}
                        onChange={() => toggle(READ)}
                      />
                    )}
                    {offered.includes(ACT) && (
                      <ScopeOption
                        label="Act on your behalf"
                        checked={chosen.includes(ACT)}
                        onChange={() => toggle(ACT)}
                      />
                    )}
                    {offered.includes(OPS) && canGrantOps && (
                      <ScopeOption
                        label="Developer access (inspect and re-run the pipeline)"
                        checked={chosen.includes(OPS)}
                        onChange={() => toggle(OPS)}
                      />
                    )}
                  </div>
                </div>
              )}

              <ul className="mt-[var(--space-6)] flex flex-col gap-[var(--space-3)]">
                {scopes.includes(READ) && (
                  <>
                    <Allowed>
                      Search everything you have captured, and read the insights and world view it
                      finds
                    </Allowed>
                    <Allowed>
                      Read your subjects, people, entities and meeting transcripts
                    </Allowed>
                  </>
                )}

                {scopes.includes(ACT) ? (
                  <>
                    {/*
                      Four bullets rather than one, because acting is four grants and three of
                      them are easy to leave unsaid. An agent that can answer the review queue can
                      also see it; it can mint a playback link, which is a link that plays the
                      recording for anybody holding it; and it can write down what the owner
                      dictates, as the owner's own word, past the review gate. A
                      token has to mean what this screen said, so this screen says all four.
                    */}
                    <Allowed>
                      See what is waiting on you: your recordings and how they are getting on, the
                      review queue, open loops, contradictions, voices still to be named, and how
                      capture is set up
                    </Allowed>
                    <Allowed>
                      Answer those things on your behalf: approve, reword or discard an insight,
                      close an open loop, decide a contradiction, name a speaker, add a note, or
                      run a failed recording again. It never decides for you &mdash; it does what
                      you tell it to, and everything it does is recorded.
                    </Allowed>
                    <Allowed>
                      Write down what you tell it: create a person, team or other entity, add a
                      belief or a relation, and see the questions Factorium has for you. What it
                      writes lands as your own word, marked &ldquo;asserted by you&rdquo; and not
                      reviewed first &mdash; so it should only ever write what you said, never its
                      own conclusions.
                    </Allowed>
                    <Allowed>
                      Give you a link to listen back to part of a recording. The link plays the
                      audio for anyone who has it, without signing in, until it expires shortly
                      afterwards.
                    </Allowed>
                  </>
                ) : (
                  <Refused>
                    Change anything. It cannot approve an insight, edit a belief, add a recording
                    or delete a thing.
                  </Refused>
                )}

                <Refused>
                  See anyone else&rsquo;s organization. It reads exactly what you can read, and
                  nothing else.
                </Refused>
              </ul>

              {/*
                Set apart rather than listed as a fourth bullet. An agent that can re-run the
                pipeline is a different kind of thing from one that reads your meetings, and a
                screen that gives both the same typography has taught somebody to skim.

                It says what a re-run destroys because that is the half a person would otherwise
                only find out afterwards: re-running from a step deletes what every later step
                produced, including transcript corrections somebody spent an evening making.
              */}
              {scopes.includes(OPS) && (
                <div className="mt-[var(--space-4)] flex items-start gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--warning)]/40 bg-[var(--warning)]/8 px-[var(--space-3)] py-[var(--space-3)]">
                  <Wrench className="mt-0.5 size-4 shrink-0 text-[var(--warning)]" aria-hidden />
                  <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">
                    <span className="font-semibold">Developer access.</span> It can look inside the
                    processing itself &mdash; what each step was given, what it produced, what
                    failed and why &mdash; change how a step is run, and run a recording again from
                    a step, which throws away everything that came after it. Only allow this for a
                    tool you are using to work on Factorium.
                  </p>
                </div>
              )}

              <p className="mt-[var(--space-6)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
                You can disconnect it at any time in Settings &rarr; Connected agents, and its
                access expires on its own if it stops being used.
              </p>

              {problem && (
                <p
                  role="alert"
                  className="mt-[var(--space-4)] flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--destructive)]/40 bg-[var(--destructive)]/8 px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--destructive)]"
                >
                  <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                  {problem}
                </p>
              )}

              <div className="mt-[var(--space-6)] flex items-center justify-end gap-[var(--space-3)]">
                <button
                  type="button"
                  onClick={() => window.close()}
                  className="rounded-[var(--radius-md)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void approve()}
                  disabled={working || nothingChosen}
                  className="flex items-center gap-[var(--space-2)] rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-6)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-60"
                >
                  <ShieldCheck className="size-4" aria-hidden />
                  {working ? "Connecting…" : "Allow access"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function Allowed({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]">
      <Check className="mt-0.5 size-4 shrink-0 text-[var(--success)]" aria-hidden />
      <span>{children}</span>
    </li>
  );
}

/** One row of the chooser (M9.6+) — an agent that asked for nothing gets one of these per
 * tier this deployment offers, and the bullets below react to whatever is ticked. */
function ScopeOption({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-sm)] text-[var(--text-primary)]">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-4 accent-[var(--primary)]"
      />
      {label}
    </label>
  );
}

function Refused({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-muted)]">
      <X className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden />
      <span>{children}</span>
    </li>
  );
}

/**
 * A link that arrived without what it is about. Said plainly rather than shown as a broken
 * consent screen, because the one thing worse than refusing an approval is collecting one for
 * a request nobody can describe.
 */
function Incomplete() {
  return (
    <div className="flex flex-col items-center gap-[var(--space-3)] text-center">
      <AlertTriangle className="size-6 text-[var(--warning)]" aria-hidden />
      <h1 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
        This link is incomplete
      </h1>
      <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
        Start the connection again from the application you were using.
      </p>
    </div>
  );
}
